import axios from 'axios';
import {
	NetxThemeMode,
	NetxThemePref,
	NETX_THEME_MODES,
} from '@netx/core-components/Theme/constants';
import type {
	ThemeMode,
	NetxThemeStyles,
	NetxCustomCssProperties,
} from '@netx/core-components/Theme/types';
import {netxThemeModeToThemeMode} from '@netx/core-components/Theme/utils/netxThemeModeToThemeMode';
import {typedEntries} from '@netx/core-next/utils/object/typedEntries';
import config from '@netx/core-app/config';
import {isBrandPortal} from '@netx/core-cms-next/utils/isBrandPortal';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {URL} from '@netx/core-next/url';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {setGlobal} from '@netx/core-next/utils/setGlobal';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';

const logger = getPrefixedLogger('css');

const customPropertyPrefix = config.CSS_CUSTOM_PROPERTY_PREFIX;

// Will store all css properties here
let currentProperties: NetxCustomCssProperties = {
	updated: '0',
	'login-background-assetId': '',
	'light-on-primary-custom': 'false',
	'light-on-secondary-custom': 'false',
	'dark-on-primary-custom': 'false',
	'dark-on-secondary-custom': 'false',
	...config.CSS_CUSTOM_PROPERTIES,
};

let currentThemeMode: ThemeMode = NetxThemeMode.LIGHT;
let fetchedCSS = false;

const documentElement = document.documentElement;
const style = documentElement.style;

/**
 * @function
 * @param key Style property key
 * @param value Style property value
 */
const _setStyleProperty = (key: string, value?: string) => {
	// setting a value
	if (value) {
		style.setProperty(key, value);
		dispatcher.trigger('css:variable:updated', key, value);
		return;
	}

	// clearing a value

	// Look for default value
	const _key = `${currentThemeMode}-${key.replace(
		customPropertyPrefix,
		'',
	)}` as keyof NetxThemeStyles;
	const defaultValue = config.CSS_CUSTOM_PROPERTIES[_key];

	defaultValue
		? style.setProperty(key, defaultValue)
		: // If no default value, remove the property
		  style.removeProperty(key);

	dispatcher.trigger('css:variable:updated', key, defaultValue);
};

/**
 * Get the current theme mode
 * @function
 * @return Theme mode
 */
export const getThemeMode = () =>
	netxThemeModeToThemeMode(getPreference(NetxThemePref.USER_SELECTED));

setGlobal({getThemeMode});

/**
 * Get style property from DOM
 * @function
 * @return Style property
 */
export const getStyleProperty = (key: string) => {
	const style = getComputedStyle(document.body);
	return style.getPropertyValue(`${customPropertyPrefix}${key}`).trim();
};

setGlobal({getStyleProperty});

/**
 * @function
 * @returns string | undefined
 */
export const getLoginBackgroundUrl = () =>
	!isBrandPortal()
		? `${URL.loginBackground}?cb=${currentProperties.updated || Date.now()}`
		: undefined;

setGlobal({getLoginBackgroundUrl});

/**
 * Changes address bar color in supported browsers
 * @function
 */
export const setMetaThemeColor = (value: string) =>
	document
		.querySelector('meta[name=theme-color]')
		?.setAttribute('content', value);

setGlobal({setMetaThemeColor});

/**
 * Set style property on DOM
 * @function
 * @param key Style property key
 * @param value Style property value
 */
export const setStyleProperty = (
	key: keyof NetxCustomCssProperties,
	value: string,
) => {
	Object.assign(currentProperties, {[key]: value});

	// We use this for cachebusting the background image since last update
	if (key === 'updated') return false;

	const simpleKey = key.replace(`${currentThemeMode}-`, '');

	if (simpleKey === 'primary') setMetaThemeColor(value);

	_setStyleProperty(`${customPropertyPrefix}${simpleKey}`, value);

	return true;
};

setGlobal({setStyleProperty});

/**
 * @function
 * @param name Theme mode name
 * @fires module:netx#theme:toggled
 */
export const setThemeMode = (
	name?: NetxThemeMode | ThemeMode,
	force = false,
) => {
	name = netxThemeModeToThemeMode(name);

	const themeChanged = currentThemeMode !== name;

	currentThemeMode = name as ThemeMode;

	typedEntries(currentProperties).forEach(([key, value]) => {
		if (
			!key.includes(`${currentThemeMode}-`) ||
			getStyleProperty(key) === value
		)
			return;
		setStyleProperty(key, value);
	});

	if (themeChanged) {
		// Clear all possible theme-mode classes
		NETX_THEME_MODES.forEach((mode) =>
			documentElement.classList.remove(`theme-${mode}`),
		);
		// Add selected theme-mode class
		document.documentElement.classList.add(`theme-${currentThemeMode}`);
	}

	// Dispatch event for the rest of the app to listen to
	(themeChanged || force) && dispatcher.trigger('theme:toggled', name);
};

setGlobal({setThemeMode});

/**
 * When user prefs are available we can set the theme mode to the user's selection
 * @event module:app#app:user:prefs:ready
 */
// TODO: these `once` dispatchers on the global scope of a file are hard to test from multiple ways
// having a hard time testing the the initialize thoroughly and this having not `fetchedCSS`
dispatcher.once('app:user:prefs:ready', () => {
	window
		.matchMedia('(prefers-color-scheme: dark)')
		.addEventListener('change', (event) => {
			if (!fetchedCSS) return;
			const themeMode = getThemeMode();
			logger.info(`os pref changed > setting theme mode > ${themeMode}`);
			setThemeMode(getThemeMode());
		});
	// we will call set theme mode when the css is done fetching
	if (!fetchedCSS) return;
	const themeMode = getThemeMode();
	logger.info(`prefs ready > setting theme mode > ${themeMode}`);
	setThemeMode(themeMode);
});

const initialize = async () => {
	// Set the default theme mode class
	documentElement.classList.add(`theme-${currentThemeMode}`);
	logger.info('initializing css...');
	// Load css properties from server
	return await axios
		.get<NetxCustomCssProperties>(
			`${URL.customCssProperties}?cb=${Date.now()}`,
			{
				responseType: 'json',
			},
		)
		.then(({data}) => {
			// Store all data
			currentProperties = {
				...config.CSS_CUSTOM_PROPERTIES,
				...data,
			};
			// Set current theme (will loop over data and set based on theme mode)
			setThemeMode(getThemeMode(), true);

			fetchedCSS = true;
			logger.info('css initialized');
		})
		.catch((ex) => {
			logger.error('failed to load css properties', ex.message);
		});
};

export default () => initialize();
